/**
 * The client-safe half of the versus-cards feature: the card types plus the pure join
 * helpers the render side needs. The keyed DealHunt fetch, caches, and sanitizer live in
 * ./versusCards (server-only: it reads `privateConfig('dealHuntSpecsApiKey')` and holds
 * module-scope caches) — same split, for the same bundle reason, as
 * bestCards/bestCardsShared. Client components import from HERE, never ./versusCards.
 */

/** Canonical topic keys — the only chips that ever render, in template order. */
export const TOPIC_KEYS = [
  'use_fit',
  'features',
  'connectivity',
  'battery',
  'anc',
  'sound',
  'microphone',
] as const
export type VersusTopicKey = (typeof TOPIC_KEYS)[number]

export interface VersusCardTopic {
  key: VersusTopicKey
  /** Fixed chip label, rendered as given ("Fit & use", "Battery"…). */
  label: string
  /** Verbatim editorial quote — may contain `[bracketed]` resolutions and `…` elisions. */
  quote: string
  /**
   * URL fragment without '#', arriving PRE-NORMALIZED with the `id-` prefix
   * (`id-battery-158275`) — byte-identical to `normalizeId`'s heading ids, so callers
   * concatenate, never re-prefix. Null → link to the plain article URL.
   */
  anchor: string | null
}

export interface VersusBucket {
  heading: string
  /** FULL list — the renderer truncates to 3 + "+n more in the article". */
  bullets: string[]
}

export interface VersusCardVerdict {
  leanQuote: string | null
  buckets: VersusBucket[]
  /** Same pre-normalized contract as `VersusCardTopic.anchor`. */
  anchor: string | null
}

export interface VersusCard {
  wpPostId: number
  /** Absolute soundguys.com article URL — the fallback join key. */
  url: string
  verdict: VersusCardVerdict | null
  topics: VersusCardTopic[]
}

/** Lookup maps over a card set — `wpPostId` primary, article URL path fallback. */
export function versusCardLookup(cards: VersusCard[]): {
  byId: Map<number, VersusCard>
  byPath: Map<string, VersusCard>
} {
  const byId = new Map<number, VersusCard>()
  const byPath = new Map<string, VersusCard>()
  for (const card of cards) {
    byId.set(card.wpPostId, card)
    const path = urlPath(card.url)
    if (path) {
      byPath.set(path, card)
    }
  }
  return { byId, byPath }
}

/** `/anker-…-158275` from either an absolute URL or an app-relative href. */
export function urlPath(url: string): string | null {
  try {
    const path = url.startsWith('http') ? new URL(url).pathname : url
    return path.replace(/\/+$/, '') || null
  } catch {
    return null
  }
}
